import React from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
  TextField,
} from "@mui/material";
import { StyledTab, StyledTabs } from "components/CustomTabs";
import { TabPanel } from "components/TabPanel";
import Div from "@jumbo/shared/Div";
import TestResultItem from "components/TestResultItem";
import {
  SUBMISSION_STATUSES,
  ALERT_SEVERITIES,
} from "app/utils/constants/constants";
import formatValue from "app/utils/formatUtils";

const TestOutputContent = ({
  problemData,
  testResults,
  isPolling,
  metadata,
  output,
  loadingMessage,
  testCaseTabValue,
  setTestCaseTabValue,
  showOutputs = false,
}) => {
  // Resolve the display status from metadata.submissionStatus
  const displayStatus = testResults
    ? metadata?.submissionStatus?.find(
        (status) => status.key === testResults.status
      )?.value || testResults.status
    : "Unknown";

  const outputContent = [
    <>
      {problemData && problemData.problemTests?.length > 0 ? (
        <>
          <StyledTabs
            value={testCaseTabValue}
            onChange={(event, newValue) => setTestCaseTabValue(newValue)}
            sx={{ display: "flex", flexDirection: "row" }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {problemData.problemTests.map((testCase, index) => (
              <StyledTab key={index} label={`Case ${index + 1}`} />
            ))}
          </StyledTabs>
          {problemData.problemTests.map((testCase, index) => (
            <TabPanel
              key={index}
              value={testCaseTabValue}
              index={index}
              sx={{ p: 0 }}
            >
              <Div sx={{ p: 2, height: "100%", overflowY: "auto" }}>
                {testCase.problemInputs?.length > 0 ? (
                  <>
                    <Typography sx={{ mb: 2 }} fontWeight="600">
                      Inputs:
                    </Typography>
                    {testCase.problemInputs.map((input, inputIndex) => (
                      <Div key={inputIndex} display="flex" sx={{ mb: 2 }}>
                        <TextField
                          key={inputIndex}
                          id="outlined-basic"
                          label={input.variableName}
                          variant="outlined"
                          value={formatValue(input.value)}
                          disabled
                          fullWidth
                          sx={{
                            "& .MuiInputLabel-root.Mui-disabled": {
                              color: "#475259",
                              fontSize: "1rem",
                              left: "-2px",
                              top: "-1px",
                            },
                            "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                              {
                                textFillColor: "#475259",
                              },
                          }}
                        />
                      </Div>
                    ))}
                  </>
                ) : (
                  <Typography sx={{ mb: 2, fontWeight: "600" }}>
                    No inputs provided.
                  </Typography>
                )}
                {showOutputs && (
                  <>
                    <Typography sx={{ mb: 2, fontWeight: "600" }}>
                      Expected Output
                    </Typography>
                    {testCase.output ||
                      (testCase.problemOutputs?.length > 0 ? (
                        testCase.problemOutputs.map((o, outputIndex) => (
                          <Div key={outputIndex} display="flex" sx={{ mb: 2 }}>
                            <TextField
                              key={outputIndex}
                              id="outlined-basic"
                              label={o.variableName}
                              variant="outlined"
                              value={formatValue(o.value)}
                              disabled
                              fullWidth
                              sx={{
                                "& .MuiInputLabel-root.Mui-disabled": {
                                  color: "#475259",
                                },
                                "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                                  {
                                    textFillColor: "#475259",
                                  },
                              }}
                            />
                          </Div>
                        ))
                      ) : (
                        <Typography sx={{ mb: 2, fontWeight: "600" }}>
                          No output provided.
                        </Typography>
                      ))}
                  </>
                )}
              </Div>
            </TabPanel>
          ))}
        </>
      ) : (
        <Typography
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minWidth: 0,
            height: "100%",
          }}
        >
          No test cases available.
        </Typography>
      )}
    </>,
    <Div
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        overflowY: "auto",
      }}
    >
      {isPolling ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            minWidth: 0,
            alignContent: "center",
            height: "100%",
          }}
        >
          <CircularProgress sx={{ m: "-40px auto 0" }} size="30px" />
          <Typography variant="body1" color="text.secondary">
            {loadingMessage}
          </Typography>
        </Box>
      ) : output ? (
        <Typography sx={{ color: "error.main" }}>{output}</Typography>
      ) : testResults ? (
        <>
          <Typography variant="body1">
            <strong>Test Summary:</strong> {testResults.numberOfSuccessfulTests}
            /{testResults.totalNumberOfTests} tests passed
          </Typography>
          {testResults.status === SUBMISSION_STATUSES.ACCEPTED ? (
            <Alert severity={ALERT_SEVERITIES.SUCCESS}>
              <AlertTitle>Success</AlertTitle>
              Your code has been accepted
            </Alert>
          ) : (
            <Alert
              severity={ALERT_SEVERITIES.ERROR}
              sx={{ width: "100%", "& .MuiAlert-message": { width: "100%" } }}
            >
              <AlertTitle sx={{ mb: 2 }}>{displayStatus}</AlertTitle>
              {testResults.compilationOutput && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <TextField
                    multiline
                    id="outlined-basic"
                    variant="outlined"
                    label="Compilation Output"
                    value={testResults.compilationOutput}
                    disabled
                    fullWidth
                    sx={{
                      "& .MuiInputLabel-root.Mui-disabled": {
                        color: "#475259",
                      },
                      "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                        {
                          textFillColor: "#475259",
                        },
                    }}
                  />
                </Typography>
              )}
              {testResults.consoleOutput && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <TextField
                    multiline
                    id="outlined-basic"
                    variant="outlined"
                    label="Console Output"
                    value={testResults.consoleOutput}
                    disabled
                    fullWidth
                    sx={{
                      "& .MuiInputLabel-root.Mui-disabled": {
                        color: "#475259",
                      },
                      "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                        {
                          textFillColor: "#475259",
                        },
                    }}
                  />
                </Typography>
              )}
              {testResults.consoleError && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <TextField
                    multiline
                    id="outlined-basic"
                    variant="outlined"
                    label="Console Error"
                    value={testResults.consoleError}
                    disabled
                    fullWidth
                    sx={{
                      "& .MuiInputLabel-root.Mui-disabled": {
                        color: "#475259",
                      },
                      "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                        {
                          textFillColor: "#475259",
                        },
                    }}
                  />
                </Typography>
              )}
            </Alert>
          )}
          {testResults.failedTests?.length > 0 && (
            <>
              <Typography variant="h6">Failed Tests:</Typography>
              {testResults.failedTests.map((test, index) => (
                <TestResultItem
                  key={index}
                  test={test}
                  index={index}
                  severity={ALERT_SEVERITIES.ERROR}
                />
              ))}
            </>
          )}
          {testResults.evaluatedTests?.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mt: 2 }}>
                Passed Tests:
              </Typography>
              {testResults.evaluatedTests.map((test, index) => (
                <TestResultItem
                  key={index}
                  test={test}
                  index={index}
                  severity={ALERT_SEVERITIES.SUCCESS}
                />
              ))}
            </>
          )}
        </>
      ) : (
        <Typography
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          No output yet. Run the code to see the result.
        </Typography>
      )}
    </Div>,
  ];

  return outputContent;
};

export default TestOutputContent;
